import React from "react";
import Slider from "react-slick";
import {
  Box,
  Container,
  Typography,
  Avatar,
  Paper,
  Rating,
  useTheme,
  alpha,
  IconButton,
} from "@mui/material";
import { motion } from "framer-motion";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const CustomArrow = ({ className, style, onClick, direction }) => (
  <IconButton
    onClick={onClick}
    sx={{
      ...style,
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      [direction === "left" ? "left" : "right"]: -20,
      zIndex: 2,
      bgcolor: "#fff",
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      "&:hover": { bgcolor: "var(--primary-color)", color: "#fff" },
      display: { xs: "none", md: "flex" },
    }}
  >
    {direction === "left" ? (
      <ArrowBackIosNewIcon fontSize="small" />
    ) : (
      <ArrowForwardIosIcon fontSize="small" />
    )}
  </IconButton>
);

const Testimonial = (props) => {
  const theme = useTheme();

  const settings = {
    dots: true,
    arrows: true,
    infinite: true,
    speed: 800,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    nextArrow: <CustomArrow direction="right" />,
    prevArrow: <CustomArrow direction="left" />,
    appendDots: (dots) => (
      <Box sx={{ mt: 4 }}>
        <ul style={{ margin: "0px", padding: "0px" }}> {dots} </ul>
      </Box>
    ),
    customPaging: (i) => (
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: "50%",
          bgcolor: "rgba(0,0,0,0.1)",
          transition: "0.3s",
          "&.Mui-active": {
            bgcolor: "var(--primary-color)",
            transform: "scale(1.2)",
          },
        }}
      />
    ),
    responsive: [
      {
        breakpoint: 1100,
        settings: { slidesToShow: 2 },
      },
      {
        breakpoint: 767,
        settings: { slidesToShow: 1, arrows: false },
      },
    ],
  };

  const testimonials = [
    {
      text: "The team handled my company registration and GST filing with complete professionalism. They explained every step clearly and made the entire process smooth and stress-free.",
    },
    {
      text: "I have been using their accounting and tax planning services for over a year. Their guidance saved me a significant amount on taxes while staying fully compliant.",
    },
    {
      text: "Their CA service is excellent—quick responses, accurate filing, and reliable advice. They helped me with income tax returns and investment planning with great clarity.",
    },
    {
      text: "Exceptional advisory on deals and mergers. Their attention to detail and market insight provided us with a competitive edge during our expansion.",
    },
  ];

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        bgcolor: "#fcfdfe",
        backgroundImage:
          "radial-gradient(circle at 2px 2px, rgba(0,0,0,0.02) 1px, transparent 0)",
        backgroundSize: "40px 40px",
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="overline"
              sx={{
                color: "var(--primary-color)",
                fontWeight: 700,
                letterSpacing: 3,
                mb: 1,
                display: "block",
              }}
            >
              Client Success Stories
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "2.2rem", md: "3.2rem" },
                fontWeight: 900,
                color: "var(--primary-color)",
                fontFamily: "'Roboto Slab', serif",
                mb: 2,
              }}
            >
              Words from our Partners
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#666",
                maxWidth: "600px",
                mx: "auto",
                lineHeight: 1.8,
                fontSize: "1.1rem",
              }}
            >
              We take pride in our commitment to excellence and client success.
              Read what our valued clients have to say about their journey with
              us.
            </Typography>
          </motion.div>
        </Box>

        <Box
          sx={{
            px: { xs: 0, md: 4 },
            position: "relative",
            "& .slick-track": { display: "flex", gap: 0 },
            "& .slick-slide": {
              height: "auto",
              display: "flex",
              "& > div": { width: "100%", display: "flex" },
            },
          }}
        >
          <Slider {...settings}>
            {testimonials.map((item, index) => (
              <Box key={index} sx={{ px: 2, py: 3, display: "flex", flex: 1 }}>
                <Paper
                  component={motion.div}
                  whileHover={{ y: -10 }}
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: "24px",
                    bgcolor: "#fff",
                    border: "1px solid #f0f0f0",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    boxShadow: "0 10px 40px rgba(0,0,0,0.03)",
                    transition: "0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    position: "relative",
                    "&:hover": {
                      boxShadow: "0 20px 50px rgba(26, 66, 161, 0.08)",
                      borderColor: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                 

                  <Typography
                    variant="body1"
                    sx={{
                      color: "#444",
                      fontStyle: "italic",
                      lineHeight: 1.8,
                      mb: 4,
                      flexGrow: 1,
                      fontSize: "1.05rem",
                    }}
                  >
                    "{item.text}"
                  </Typography>

                  
                </Paper>
              </Box>
            ))}
          </Slider>
        </Box>
      </Container>
    </Box>
  );
};

export default Testimonial;
